import { FC, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'lib/axios'
import { useNotification } from 'lib/context/notification.context'
import QuestionaryForm, {
  QuestionnaireFormValues,
  QuestionnairePayload,
} from 'views/components/Forms/cuestionary'
import TitleDiv from 'views/components/UI/TitleDiv'
import { MainLayout } from 'views/layouts'

type QuestionnaireOptionResponse = {
  id: number
  text: string
  position: number
}

type QuestionnaireQuestionResponse = {
  id: number
  text: string
  type: string
  is_required: boolean
  position: number
  options?: QuestionnaireOptionResponse[]
}

type QuestionnaireResponse = {
  id: number
  name: string
  description: string | null
  status: string
  can_edit?: boolean
  questions: QuestionnaireQuestionResponse[]
}

const EditBasicInfoQuestionnaire: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { getError, getWarning } = useNotification()
  const [questionnaire, setQuestionnaire] = useState<QuestionnaireResponse | null>(
    null
  )

  const { questionnaireId, questionnaireName } = location.state || {}

  useEffect(() => {
    if (!questionnaireId) return

    axios
      .get(`/company-questionnaire/${questionnaireId}`)
      .then((response) => {
        const nextQuestionnaire = response?.data?.questionnaire || null

        if (nextQuestionnaire?.can_edit === false) {
          getWarning('No puedes editar este cuestionario')
          navigate(-1)
          return
        }

        setQuestionnaire(nextQuestionnaire)
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          'No fue posible cargar el cuestionario para edición'
        getError(message)
      })
  }, [getError, getWarning, navigate, questionnaireId])

  const initialValues = useMemo<QuestionnaireFormValues | undefined>(() => {
    if (!questionnaire) return undefined

    return {
      name: questionnaire.name || '',
      description: questionnaire.description || '',
      status: questionnaire.status || 'draft',
      questions:
        questionnaire.questions
          ?.slice()
          .sort((first, second) => first.position - second.position)
          .map((question) => ({
            id: question.id,
            text: question.text || '',
            type: question.type,
            isRequired: Boolean(question.is_required),
            options: (question.options || [])
              .slice()
              .sort((first, second) => first.position - second.position)
              .map((option) => ({
                id: option.id,
                text: option.text || '',
              })),
          })) || [],
    }
  }, [questionnaire])

  const handleUpdate = async (payload: QuestionnairePayload) => {
    await axios.put(`/company-questionnaire/${questionnaireId}`, payload)
  }

  return (
    <MainLayout>
      <TitleDiv>
        <h2 style={{ fontSize: 32 }}>
          Editar cuestionario {questionnaire?.name || questionnaireName || ''}
        </h2>
      </TitleDiv>

      {initialValues ? (
        <QuestionaryForm
          initialValues={initialValues}
          onSubmit={handleUpdate}
          submitLabel="Actualizar cuestionario"
          successMessage="Cuestionario actualizado con éxito"
          errorMessage="No fue posible actualizar el cuestionario"
        />
      ) : null}
    </MainLayout>
  )
}

export default EditBasicInfoQuestionnaire
